import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePost } from '../../../lib/enterprise-client';
import { API_KEY_ENV_VAR, maskKey, resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { dim, printJson, printWarn, sanitizeForTerminal, type OutputFormat } from '../../../lib/output';

// 對齊 server ENTERPRISE_KEY_EXPIRY_DAYS（enterprise-api-key.constants.ts）。CLI 是獨立套件、
// 不能 import server code，故在此複製一份；server 端 @IsIn 才是唯一權威，本地只做提前擋。
const ENTERPRISE_KEY_EXPIRY_DAYS = [30, 60, 90] as const;

interface RotateFlags {
	expiryDays?: number;
	reveal?: boolean;
}

/** 回應對齊 server EnterpriseKeyIssuedVm（enterprise-key.vm.ts，pm_41 KEY-M-2）。 */
export interface EnterpriseKeyIssued {
	api_key?: string;
	enc_id?: string;
	name?: string | null;
	scopes?: string[];
	expires_at?: string | null;
	key_last4?: string | null;
	[k: string]: unknown;
}

/** --expiry-days 若帶，必須是 30/60/90（server EnterpriseRotateKeyDto @IsIn）。本地先擋，省一次請求。 */
export function validateExpiryDays(raw: number | undefined): number | undefined {
	if (raw === undefined) return undefined;
	if (!(ENTERPRISE_KEY_EXPIRY_DAYS as readonly number[]).includes(raw)) {
		throw new CliError(
			`Invalid --expiry-days ${raw}. Allowed: ${ENTERPRISE_KEY_EXPIRY_DAYS.join(', ')}`,
			ExitCode.InvalidArgument
		);
	}
	return raw;
}

/**
 * `keys rotate` 核心：POST /keys/:enc_id/rotate → 回新明文（僅一次）。與 commander 分離便於測試。
 *
 * 注意：呼叫端已 resolveApiKey，**不可**在本地擋過期 key —— rotate 是唯一「拿過期 key
 * 當 Bearer 仍可過」的端點（server EnterpriseApiKeyRotateGuard，pm_41 §3.5）。
 */
export async function runKeysRotate(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string,
	encId: string,
	flags: RotateFlags
): Promise<void> {
	const trimmed = encId.trim();
	if (!trimmed) throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	const expiryDays = validateExpiryDays(flags.expiryDays);

	const requestBody: Record<string, unknown> = {};
	if (expiryDays !== undefined) requestBody.expiry_days = expiryDays;

	const { body } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/keys/${encodeURIComponent(trimmed)}/rotate`,
		requestBody
	);
	const issued = unwrapDataResponse<EnterpriseKeyIssued>(body);

	if (ctx.format === 'json') {
		// json 模式帶完整明文 api_key（供腳本擷取，PRD §6.7/§12.1）。
		printJson(issued);
	} else {
		const plaintext = issued.api_key ?? '';
		const shown = flags.reveal ? plaintext : maskKey(plaintext);
		const lines = [
			`New API key: ${sanitizeForTerminal(shown)}`,
			`enc_id:      ${sanitizeForTerminal(issued.enc_id ?? '')}`,
			`scopes:      ${sanitizeForTerminal((issued.scopes ?? []).join(','))}`,
			`expires_at:  ${sanitizeForTerminal(issued.expires_at ?? '')}`,
		];
		process.stdout.write(lines.join('\n') + '\n');
		if (!flags.reveal) {
			process.stdout.write(dim('Re-run with --reveal to print the full key once.', ctx.color) + '\n');
		}
	}

	// 舊 key 立即失效（PRD §12.1）：務必提醒更新憑證，否則下次呼叫用舊 key 會 401。stderr 不污染 json stdout。
	printWarn(
		`The previous key is now invalid. Update your ${API_KEY_ENV_VAR} env var / credential store, ` +
			'or run "wport enterprise login" with the new key.',
		ctx.color
	);
}

export function registerEnterpriseKeysRotate(parent: Command): void {
	parent
		.command('rotate <enc_id>')
		.description('Rotate an API key in place (issues a new key, invalidates the old one)')
		.option('--expiry-days <n>', 'new key lifetime in days (30 | 60 | 90; default 90)', (v) => Number(v))
		.option('--reveal', 'print the full new key (default masks all but the last 4 chars)')
		.action(async (encId: string, flags: RotateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			// resolveApiKey 只驗格式，不驗到期 —— 過期 key 仍是合法 wpk_live_ 格式，會放行（rotate 需要）。
			const { key } = resolveApiKey(globals.apiKey);
			await runKeysRotate(ctx, key, encId, flags);
		});
}
